import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";

import AgriActionButton from "components/AgriTheme/AgriActionButton";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenMobileSidenav,
} from "context";

function Sidenav({ brandName, routes }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, openMobileSidenav } = controller;
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);

  useEffect(() => {
    const syncSidenavWithViewport = () => {
      const mobileViewport = window.innerWidth < 1200;
      setIsMobile(mobileViewport);
      setMiniSidenav(dispatch, mobileViewport ? false : window.innerWidth < 1400);
      if (!mobileViewport) {
        setOpenMobileSidenav(dispatch, false);
      }
    };

    syncSidenavWithViewport();
    window.addEventListener("resize", syncSidenavWithViewport);

    return () => window.removeEventListener("resize", syncSidenavWithViewport);
  }, [dispatch]);

  const pathname = location.pathname;

  useEffect(() => {
    if (isMobile) {
      setOpenMobileSidenav(dispatch, false);
    }
  }, [dispatch, isMobile, pathname]);

  const renderRoutes = routes.map(({ type, name, icon, key, route }) => {
    if (type !== "collapse") {
      return null;
    }

    const active = pathname === route || pathname.startsWith(`${route}/`);

    return (
      <NavLink
        key={key}
        to={route}
        onClick={() => {
          if (isMobile) {
            setOpenMobileSidenav(dispatch, false);
          }
        }}
      >
        <SidenavCollapse name={name} icon={icon} active={active} />
      </NavLink>
    );
  });

  return (
    <SidenavRoot
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? openMobileSidenav : true}
      onClose={() => setOpenMobileSidenav(dispatch, false)}
      ModalProps={{
        keepMounted: true,
      }}
      ownerState={{
        miniSidenav,
        isMobile,
      }}
    >
      <Box
        px={miniSidenav ? 1 : 1.2}
        pt={1.25}
        pb={1.5}
        display="flex"
        alignItems="center"
        justifyContent={miniSidenav && !isMobile ? "center" : "space-between"}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent={miniSidenav && !isMobile ? "center" : "flex-start"}
          gap={1.25}
          overflow="hidden"
        >
          <Box
            width="44px"
            height="44px"
            borderRadius="16px"
            display="grid"
            placeItems="center"
            sx={{
              background:
                "linear-gradient(135deg, rgba(94,168,255,0.95) 0%, rgba(76,195,138,0.95) 100%)",
              boxShadow: "0 14px 28px rgba(0,0,0,0.32)",
            }}
          >
            <Typography fontSize="1.1rem" fontWeight={800} color="#fff">
              AQ
            </Typography>
          </Box>
          {!miniSidenav || isMobile ? (
            <Box>
              <Typography fontSize="1rem" fontWeight={800} color="#F7FAF4">
                {brandName}
              </Typography>
              <Typography fontSize="0.72rem" color="rgba(230,239,231,0.62)">
                Admin dashboard
              </Typography>
            </Box>
          ) : null}
        </Box>

        {isMobile ? (
          <Box
            component="button"
            type="button"
            onClick={() => setOpenMobileSidenav(dispatch, false)}
            sx={{
              border: "none",
              width: 38,
              height: 38,
              borderRadius: "14px",
              background: "rgba(255,255,255,0.08)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            <Icon>close</Icon>
          </Box>
        ) : !miniSidenav ? (
          <Box
            component="button"
            type="button"
            onClick={() => setMiniSidenav(dispatch, !miniSidenav)}
            sx={{
              border: "none",
              width: 38,
              height: 38,
              borderRadius: "14px",
              background: "rgba(255,255,255,0.08)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            <Icon>first_page</Icon>
          </Box>
        ) : null}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 1.5 }} />

      {miniSidenav && !isMobile ? (
        <Box px={1} mb={1.25}>
          <Box
            component="button"
            type="button"
            onClick={() => setMiniSidenav(dispatch, !miniSidenav)}
            sx={{
              width: "100%",
              height: 42,
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "14px",
              background: "rgba(255,255,255,0.05)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            <Icon>last_page</Icon>
          </Box>
        </Box>
      ) : null}

      <Box
        px={miniSidenav && !isMobile ? 0.5 : 0.25}
        sx={{
          overflowY: "auto",
          flex: 1,
        }}
      >
        <List>{renderRoutes}</List>
      </Box>

      <Box
        mt="auto"
        pt={1.5}
        sx={{
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {!miniSidenav || isMobile ? (
          <Box px={1}>
            <Typography fontSize="0.75rem" color="rgba(203,221,246,0.62)" mb={1}>
              Manage subjects, users, support, tests and content from one place.
            </Typography>
            <AgriActionButton
              fullWidth
              color="warning"
              onClick={() => {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("admin_details");
                window.location.assign("/signin");
              }}
            >
              Logout
            </AgriActionButton>
          </Box>
        ) : (
          <Box display="flex" justifyContent="center">
            <Box
              component="button"
              type="button"
              onClick={() => {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("admin_details");
                window.location.assign("/signin");
              }}
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                border: "1px solid rgba(255,255,255,0.1)",
                background: "rgba(255,255,255,0.05)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
              }}
            >
              <Icon>logout</Icon>
            </Box>
          </Box>
        )}
      </Box>
    </SidenavRoot>
  );
}

Sidenav.propTypes = {
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
